import { searchMedicine } from './api.js';
import { renderCard, showIngredientsModal, showSection } from './ui.js';
import { initAuth } from './auth.js';
import { initScriptChecker } from './script-checker.js';
import { capitalise, esc } from './utils.js';

/**
 * Fetches feature flags from the public endpoint and hides UI sections accordingly.
 * Fails-open: if the fetch fails for any reason, all features remain visible.
 */
async function applyFeatureFlags() {
  try {
    const res = await fetch('/api/public-flags');
    if (!res.ok) return; // fail-open
    const flags = await res.json();

    window.medipricePublicFlags = flags;

    // 1. Handle Global Pro Visibility (based on scriptChecker existence)
    if (flags.scriptChecker === false) {
      // Pro/script features remain hidden (they start hidden in HTML/CSS).
      // We only need to scrub any stray "Pro" labels that are not behind pro-price-hideable spans.
      const modalTitle = document.getElementById('auth-modal-title');
      if (modalTitle && modalTitle.textContent.includes('Pro')) {
        modalTitle.textContent = modalTitle.textContent.replace(' Pro', '');
      }
    } else {
      // Script Checker / Pro is enabled: reveal gated UI and mark document as pro-enabled for CSS.
      document.documentElement.classList.add('pro-enabled');

      // Script checker section
      const section = document.getElementById('script-checker');
      if (section) section.hidden = false;

      // Nav links
      document.querySelectorAll('a[href="#script-checker"]').forEach(el => el.hidden = false);
      document.querySelectorAll('.mobile-nav__link[href="#script-checker"]').forEach(el => el.hidden = false);

      // Auth / Upgrade buttons
      const navAuthBtn = document.getElementById('nav-auth-btn');
      const mobileAuthBtn = document.getElementById('mobile-auth-btn');
      const upgradeBtn = document.getElementById('auth-upgrade-btn');
      const navUpgrade = document.getElementById('nav-upgrade-btn');
      const mobileUpgrade = document.getElementById('mobile-upgrade-btn');
      
      if (navAuthBtn) navAuthBtn.hidden = false;
      if (mobileAuthBtn) mobileAuthBtn.hidden = false;
      if (upgradeBtn) upgradeBtn.hidden = false;
      
      // Only show Upgrade buttons in nav if NOT pro
      const isPro = localStorage.getItem('mediprice_pro') === 'true';
      if (navUpgrade) navUpgrade.hidden = isPro;
      if (mobileUpgrade) mobileUpgrade.hidden = isPro;

      // Account feature rows & upgrade card
      const featureScript = document.getElementById('feature-script-checker');
      const featureBest = document.getElementById('feature-best-price');
      const upgradeCard = document.getElementById('upgrade-card');
      if (featureScript) featureScript.hidden = false;
      if (featureBest) featureBest.hidden = false;
      if (upgradeCard) upgradeCard.hidden = false;
    }

    // 2. Handle Best Price specifically
    if (flags.bestPrice === false) {
      // Keep Best Price option and cards hidden (they start hidden in HTML/CSS or via pro gating).
    } else {
      // Best Price is enabled: show the "All schemes / Best Price" option if present.
      const schemeSelect = document.getElementById('script-scheme');
      if (schemeSelect) {
        const allOption = schemeSelect.querySelector('option[value="all"]');
        if (allOption) allOption.hidden = false;
      }
    }

    // 3. Update Pro price displays (if Pro IS enabled)
    if (flags.scriptChecker !== false) {
      if (flags.proPrice) {
        document.querySelectorAll('.pro-price-display').forEach(el => {
          const isAmountOnly = el.classList.contains('amount') || el.dataset.format === 'amount';
          const formattedPrice = isAmountOnly ? `${flags.proPrice}` : `R${flags.proPrice}`;
          const freq = el.dataset.frequency || '';
          el.textContent = `${formattedPrice}${freq}`;
        });
      }
      if (flags.ondemandAmount !== undefined) {
        document.querySelectorAll('.ondemand-amount-display').forEach(el => {
          el.textContent = flags.ondemandAmount;
        });
      }
      if (flags.ondemandPrice) {
        document.querySelectorAll('.ondemand-price-display').forEach(el => {
          const isAmountOnly = el.classList.contains('amount') || el.dataset.format === 'amount';
          const formattedPrice = isAmountOnly ? `${flags.ondemandPrice}` : `R${flags.ondemandPrice}`;
          const freq = el.dataset.frequency || '';
          el.textContent = `${formattedPrice}${freq}`;
        });
      }
    }

    // Dispatch event after DOM has been updated so listeners see correct state
    window.dispatchEvent(new CustomEvent('mediprice-public-flags', { detail: flags }));
  } catch {
    // Fail-open: any error silently continues
  }
}

// Quick helper to prevent long-running tasks from blocking the UI
const timeout = ms => new Promise(resolve => setTimeout(resolve, ms));

export async function initApp() {
  // Run feature flags in background so LCP isn't blocked by /api/public-flags (fail-open after 1.2s)
  Promise.race([applyFeatureFlags(), timeout(1200)]).catch(() => {});

  initAuth();
  initScriptChecker();

  const yearEl = document.getElementById('year');
  if (yearEl) yearEl.textContent = new Date().getFullYear();

  const form = document.querySelector('.search-form');
  const input = document.querySelector('.search-input');
  const submitBtn = document.getElementById('search-submit-btn');
  const notFoundEl = document.getElementById('not-found');
  const pharmBtn = document.querySelector('.pharmacy-btn');
  const backBtn = document.getElementById('back-btn');
  const searchAgainBtn = document.getElementById('search-again');
  const filterWrap = document.getElementById('results-filters');
  const listEl = document.querySelector('.results-list');
  const countEl = document.querySelector('.results-count');
  const titleEl = document.querySelector('.results-title');
  const resultsEl = document.getElementById('results');

  let currentResults = [];
  let activeTypeFilter = null;
  let activeFormFilter = null;
  let activeSort = 'relevance';

  if (!form || !input) return;

  function setLoading(on) {
    submitBtn?.classList.toggle('search-submit--loading', on);
    if (submitBtn) submitBtn.disabled = on;
    if (submitBtn) submitBtn.setAttribute('aria-busy', String(on));
  }

  form.addEventListener('submit', async e => {
    e.preventDefault();
    const q = input.value.trim();
    if (!q) { input.focus(); return; }
    await runSearch(q);
  });

  async function runSearch(q) {
    input.value = q;
    listEl.innerHTML = '';
    countEl.textContent = '';
    if (filterWrap) {
      filterWrap.hidden = true;
      filterWrap.innerHTML = '';
    }
    setLoading(true);

    try {
      const list = await searchMedicine(q);

      if (!list.length) {
        notFoundEl.querySelector('.not-found-message').textContent =
          "We don't have prices for that search. Double-check the spelling or try a different name.";
        showSection('not-found');
      } else {
        currentResults = list;
        activeTypeFilter = null;
        activeFormFilter = null;
        activeSort = 'relevance';
        titleEl.textContent = `Results for "${capitalise(q)}"`;
        initFilters(list);
        renderFilteredResults();
        showSection('results');
        resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch {
      notFoundEl.querySelector('.not-found-message').textContent =
        'Could not load results. Please check your connection and try again.';
      showSection('not-found');
    } finally {
      setLoading(false);
    }
  }

  function initFilters(list) {
    if (!filterWrap) return;
    const forms = [...new Set(list.map(m => m.dosage_form).filter(Boolean))].sort();
    
    // We always show Type filters if there's any result, or we can hide if list < 2
    if (list.length < 2) {
      filterWrap.hidden = true;
      filterWrap.innerHTML = '';
      return;
    }

    // --- Type Filter Group ---
    const typeAllBtn = `<button type="button" class="filter-chip is-active" data-type="all">All Types</button>`;
    const genericBtn = `<button type="button" class="filter-chip" data-type="generic">Generic</button>`;
    const originalBtn = `<button type="button" class="filter-chip" data-type="original">Original</button>`;
    const typeFiltersHtml = `<div class="filter-group filter-group--type">${typeAllBtn}${genericBtn}${originalBtn}</div>`;

    // --- Form Filter Group ---
    let formFiltersHtml = '';
    if (forms.length > 1) {
      const formAllBtn = `<button type="button" class="filter-chip is-active" data-form="all">All Forms</button>`;
      const formChips = forms.map(f => `<button type="button" class="filter-chip" data-form="${esc(f)}">${capitalise(f)}</button>`).join('');
      formFiltersHtml = `<div class="filter-group filter-group--form">${formAllBtn}${formChips}</div>`;
    }

    // --- Sort Group ---
    const sortHtml = `
      <div class="filter-group filter-group--sort">
        <select class="sort-select" id="sort-select" aria-label="Sort results">
          <option value="relevance">Sort by: Relevance</option>
          <option value="price-asc">Price (Low to High)</option>
          <option value="price-desc">Price (High to Low)</option>
          <option value="name-asc">Name (A-Z)</option>
          <option value="name-desc">Name (Z-A)</option>
        </select>
      </div>`;

    filterWrap.innerHTML = typeFiltersHtml + formFiltersHtml + sortHtml;
    filterWrap.hidden = false;

    // Attach listeners for Type
    filterWrap.querySelectorAll('.filter-group--type .filter-chip').forEach(chip => {
      chip.addEventListener('click', () => {
        const type = chip.dataset.type;
        activeTypeFilter = type === 'all' ? null : type;
        
        filterWrap.querySelectorAll('.filter-group--type .filter-chip').forEach(btn => 
          btn.classList.toggle('is-active', btn.dataset.type === type)
        );
        renderFilteredResults();
      });
    });

    // Attach listeners for Form
    filterWrap.querySelectorAll('.filter-group--form .filter-chip').forEach(chip => {
      chip.addEventListener('click', () => {
        const form = chip.dataset.form;
        activeFormFilter = form === 'all' ? null : form;
        
        filterWrap.querySelectorAll('.filter-group--form .filter-chip').forEach(btn => 
          btn.classList.toggle('is-active', btn.dataset.form === form)
        );
        renderFilteredResults();
      });
    });

    // Attach listener for Sort
    const sortSelect = filterWrap.querySelector('#sort-select');
    if (sortSelect) {
      sortSelect.value = activeSort;
      sortSelect.addEventListener('change', (e) => {
        activeSort = e.target.value;
        renderFilteredResults();
      });
    }
  }

  function renderFilteredResults() {
    let list = [...currentResults];
    
    if (activeSort === 'name-asc') {
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else if (activeSort === 'name-desc') {
      list.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
    } else if (activeSort === 'price-asc') {
      list.sort((a, b) => {
        const pA = parseFloat(String(a.sep || '0').replace(/[R\s,]/g, '')) || 0;
        const pB = parseFloat(String(b.sep || '0').replace(/[R\s,]/g, '')) || 0;
        return pA - pB;
      });
    } else if (activeSort === 'price-desc') {
      list.sort((a, b) => {
        const pA = parseFloat(String(a.sep || '0').replace(/[R\s,]/g, '')) || 0;
        const pB = parseFloat(String(b.sep || '0').replace(/[R\s,]/g, '')) || 0;
        return pB - pA;
      });
    }

    if (activeTypeFilter) {
      const isReqGeneric = activeTypeFilter === 'generic';
      list = list.filter(m => {
        const isMGeneric = m.is_generic === true || m.is_generic === 'Generic';
        return isMGeneric === isReqGeneric;
      });
    }
    if (activeFormFilter) {
      list = list.filter(m => m.dosage_form === activeFormFilter);
    }

    countEl.textContent = `${list.length} result${list.length !== 1 ? 's' : ''}`;
    listEl.innerHTML = list.map(renderCard).join('');
    attachCardListeners();
  }

  function attachCardListeners() {
    listEl.querySelectorAll('.medicine-card').forEach(card => {
      const openModal = () => showIngredientsModal(card.dataset.name, card.dataset.ingredients || '[]');
      card.addEventListener('click', () => openModal());
      card.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openModal(); } });
    });
  }

  document.querySelectorAll('.quick-tag').forEach(tag => {
    tag.addEventListener('click', () => {
      const q = tag.dataset.q;
      input.value = q;
      runSearch(q);
    });
  });

  const goBack = e => {
    if (e) e.preventDefault();
    if (filterWrap) {
      filterWrap.hidden = true;
      filterWrap.innerHTML = '';
    }
    currentResults = [];
    activeTypeFilter = null;
    activeFormFilter = null;
    activeSort = 'relevance';
    showSection('hero');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => input.focus(), 300);
  };
  if (backBtn) backBtn.addEventListener('click', goBack);
  if (searchAgainBtn) searchAgainBtn.addEventListener('click', goBack);

  if (pharmBtn) {
    pharmBtn.addEventListener('click', () => {
      if (!navigator.geolocation) {
        window.open('https://www.google.com/maps/search/pharmacy+near+me', '_blank');
        return;
      }
      navigator.geolocation.getCurrentPosition(
        ({ coords: { latitude: lat, longitude: lng } }) =>
          window.open(`https://www.google.com/maps/search/pharmacy/@${lat},${lng},15z`, '_blank'),
        () => window.open('https://www.google.com/maps/search/pharmacy+near+me', '_blank')
      );
    });
  }

  document.querySelectorAll('a[href^="#"]').forEach(a => {
    a.addEventListener('click', e => {
      const id = a.getAttribute('href').slice(1);
      const el = document.getElementById(id);
      if (el) { e.preventDefault(); el.scrollIntoView({ behavior: 'smooth' }); }
    });
  });

  const menuBtn = document.querySelector('.mobile-menu-btn');
  const mobileNav = document.getElementById('mobile-nav');
  const navClose = document.querySelector('.mobile-nav__close');
  const backdrop = document.querySelector('.mobile-nav__backdrop');

  const openMenu = () => { mobileNav?.classList.add('is-open'); menuBtn?.setAttribute('aria-expanded', 'true'); };
  const closeMenu = () => { mobileNav?.classList.remove('is-open'); menuBtn?.setAttribute('aria-expanded', 'false'); };

  menuBtn?.addEventListener('click', openMenu);
  navClose?.addEventListener('click', closeMenu);
  backdrop?.addEventListener('click', closeMenu);
  document.querySelectorAll('.mobile-nav__link').forEach(l => l.addEventListener('click', closeMenu));

  const scrollTopBtn = document.getElementById('scroll-top');
  if (scrollTopBtn) {
    window.addEventListener('scroll', () => {
      scrollTopBtn.classList.toggle('is-visible', window.scrollY > 400);
    }, { passive: true });
    scrollTopBtn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
  }

  // Deep-linking: allow landing pages (and shares) to open the homepage with a prefilled search.
  // Example: /?q=paracetamol
  try {
    const q = new URLSearchParams(window.location.search).get('q');
    const initial = (q || '').trim();
    if (initial) runSearch(initial);
  } catch {
    // Ignore.
  }
  // #9: Merge PWA Install listeners into main init flow
  ['pwa-install-btn', 'nav-install-btn', 'footer-install-btn', 'hero-install-chip-btn'].forEach(id => {
    document.getElementById(id)?.addEventListener('click', triggerInstall);
  });

  document.getElementById('hero-install-chip-close')?.addEventListener('click', () => {
    const chip = document.getElementById('hero-install-chip');
    if (chip) chip.hidden = true;
  });

  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
  const isStandalone = window.navigator.standalone === true;
  if (isIos && !isStandalone) {
    const iosEl = document.getElementById('install-ios');
    if (iosEl) iosEl.hidden = false;
    const chip = document.getElementById('hero-install-chip');
    const chipText = document.getElementById('hero-install-chip-text');
    const chipBtn = document.getElementById('hero-install-chip-btn');
    if (chip) chip.hidden = false;
    if (chipText) chipText.textContent = 'Add to Home Screen via Share ⎙';
    if (chipBtn) chipBtn.textContent = 'How?';
    chipBtn?.addEventListener('click', () => {
      document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' });
    });
  }
}

// Global bootstrap
document.addEventListener('DOMContentLoaded', () => initApp().catch(console.error));

// PWA install prompt handler
let deferredInstallPrompt = null;
window.addEventListener('beforeinstallprompt', e => {
  e.preventDefault();
  deferredInstallPrompt = e;
  document.querySelectorAll('#pwa-install-btn, #nav-install-btn, #footer-install-btn').forEach(btn => {
    if (btn) btn.hidden = false;
  });
  const chip = document.getElementById('hero-install-chip');
  if (chip) chip.hidden = false;
});

async function triggerInstall() {
  if (!deferredInstallPrompt) return;
  deferredInstallPrompt.prompt();
  const { outcome } = await deferredInstallPrompt.userChoice;
  if (outcome === 'accepted') {
    deferredInstallPrompt = null;
    document.querySelectorAll('#pwa-install-btn, #nav-install-btn, #footer-install-btn').forEach(btn => {
      if (btn) btn.hidden = true;
    });
    const chip = document.getElementById('hero-install-chip');
    if (chip) chip.hidden = true;
  }
}



window.addEventListener('appinstalled', () => {
  deferredInstallPrompt = null;
  document.querySelectorAll('#pwa-install-btn, #nav-install-btn, #footer-install-btn').forEach(btn => {
    if (btn) btn.hidden = true;
  });
  const chip = document.getElementById('hero-install-chip');
  if (chip) chip.hidden = true;
});

// Service Worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then(reg => {
      reg.addEventListener('updatefound', () => {
        const newWorker = reg.installing;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // #8: SW Update Strategy (Prompt User)
            const toast = document.createElement('div');
            toast.className = 'update-toast';
            toast.innerHTML = `
              <div style="background:#0f172a; color:#fff; padding:12px 20px; border-radius:8px; display:flex; gap:16px; align-items:center; position:fixed; bottom:20px; left:50%; transform:translateX(-50%); z-index:99999; box-shadow:0 10px 15px -3px rgba(0,0,0,0.1); font-size:0.9rem;">
                <span style="font-weight:500;">New version available!</span>
                <button style="background:#f0195f; color:#fff; border:none; padding:6px 12px; border-radius:6px; cursor:pointer; font-weight:600; font-size:0.85rem;">Refresh</button>
              </div>`;
            document.body.appendChild(toast);
            toast.querySelector('button').addEventListener('click', () => {
              newWorker.postMessage('SKIP_WAITING');
            });
          }
        });
      });
    }).catch(() => {});

    let refreshing;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  });
}
